// Native replacements for the three Google Forms (Observation, Accident form, News): questions, validation and the
// exact Firestore documents the Apps Script sync writes today. See docs/native-forms.md.
//
// Pure and TS 4.9 compatible (no Firebase/Node imports): the teacher portal imports the option lists and the
// validation (same questions, same required flags in the browser), and the callables in nativeFormsFunctions.ts
// build the documents with it.

export const TEACHER_IDS = ["olesia", "yasmin", "alex"] as const;

export const OBSERVATION_CATEGORIES = [
  "Physical", "Emotional", "Practical", "Social", "Sensorial", "Maths", "Language", "Factual", "Cultural", "Creative",
] as const;

export const FAMILIARITY_OPTIONS = [
  "Interested - wants to do it",
  "Introduced - shown how",
  "Engaged - tries properly unaided",
  "Performing - completes self",
  "Mastered - does it perfectly",
] as const;

export const CONCENTRATION_OPTIONS = [
  "Disordered", "Distracted", "Working", "Concentrating", "Deeply concentrating",
] as const;

export const ACCIDENT_CATEGORIES = ["Accident", "Medication"] as const;

/** Prefix of every id created here; the Google Forms ids are response ids like "2_ABaOnuc...". */
export const NATIVE_ID_PREFIX = "native_";

export interface ObservationInput {
  childIds: string[];
  teacherId: string;
  category: string;
  familiarity: string;
  concentration: string;
  observationNotes: string;
  nextSteps: string;
  /** YYYY-MM-DD */
  observationDate: string;
  /** "" or a Drive link like https://drive.google.com/open?id=... */
  photoFileUrl: string;
}

export interface AccidentInput {
  childId: string;
  teacherId: string;
  category: string;
  details: string;
  location: string;
  followUpSteps: string;
  /** YYYY-MM-DD */
  date: string;
}

export interface NewsInput {
  title: string;
  content: string;
  /** YYYY-MM-DD */
  date: string;
  photoFileUrl: string;
}

export type FieldErrors = { [field: string]: string };

type Result<T> = { ok: true; value: T } | { ok: false; errors: FieldErrors };

const REQUIRED = "This is a required question";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The Apps Script trims every string cell (AutoMap). */
const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const isOneOf = (options: readonly string[], value: string): boolean => options.indexOf(value) >= 0;

/** A real calendar date written YYYY-MM-DD. */
export const isYmd = (value: unknown): value is string => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  if (y < 2000 || y > 2100) return false;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
};

/** Google Forms file upload cells hold https://drive.google.com/open?id=<fileId>. */
export const isDriveFileUrl = (value: string): boolean => /^https:\/\/drive\.google\.com\/open\?id=[\w-]{10,}$/.test(value);

const lastSundayOf = (year: number, monthIndex: number): number => {
  const last = new Date(Date.UTC(year, monthIndex + 1, 0));
  return last.getUTCDate() - last.getUTCDay();
};

/**
 * The instant of midnight at the start of the given calendar day in Europe/London, as an ISO string. This is what
 * the sheet stores for a form date answer (the Apps Script runs in London time): "2025-05-08" is
 * "2025-05-07T23:00:00.000Z" in summer time and "2026-03-06T00:00:00.000Z" in winter.
 * Summer time starts 01:00 UTC on the last Sunday of March and ends 01:00 UTC on the last Sunday of October, so a
 * midnight is summer time for days after the March Sunday up to and including the October Sunday.
 */
export const londonMidnightIso = (ymd: string): string => {
  const [y, m, d] = ymd.split("-").map(Number);
  const midnightUtc = Date.UTC(y, m - 1, d);
  const bstStart = Date.UTC(y, 2, lastSundayOf(y, 2));
  const bstEnd = Date.UTC(y, 9, lastSundayOf(y, 9));
  const summer = midnightUtc > bstStart && midnightUtc <= bstEnd;
  return new Date(summer ? midnightUtc - 60 * 60 * 1000 : midnightUtc).toISOString();
};

const requiredText = (raw: Record<string, unknown>, field: string, errors: FieldErrors): string => {
  const value = text(raw[field]);
  if (!value) errors[field] = REQUIRED;
  return value;
};

const requiredChoice = (raw: Record<string, unknown>, field: string, options: readonly string[], errors: FieldErrors): string => {
  const value = text(raw[field]);
  if (!value) errors[field] = REQUIRED;
  else if (!isOneOf(options, value)) errors[field] = "Choose one of the listed options";
  return value;
};

const requiredDate = (raw: Record<string, unknown>, field: string, errors: FieldErrors): string => {
  const value = raw[field];
  if (value === undefined || value === null || value === "") errors[field] = REQUIRED;
  else if (!isYmd(value)) errors[field] = "Enter a valid date";
  return typeof value === "string" ? value : "";
};

const optionalPhoto = (raw: Record<string, unknown>, errors: FieldErrors): string => {
  const value = text(raw.photoFileUrl);
  if (value && !isDriveFileUrl(value)) errors.photoFileUrl = "Photo must be a Google Drive file link";
  return value;
};

/** `knownChildIds` is the list of children the portal offers (null skips the membership check, for the browser). */
export const validateObservation = (data: unknown, knownChildIds: readonly string[] | null): Result<ObservationInput> => {
  const raw = isRecord(data) ? data : {};
  const errors: FieldErrors = {};

  const picked = Array.isArray(raw.childIds) ? raw.childIds.map(text).filter(Boolean) : [];
  const childIds = picked.filter((id, index) => picked.indexOf(id) === index);
  if (childIds.length === 0) errors.childIds = REQUIRED;
  else if (knownChildIds && childIds.some((id) => !isOneOf(knownChildIds, id))) errors.childIds = "Unknown child";

  const value: ObservationInput = {
    childIds: knownChildIds ? knownChildIds.filter((id) => isOneOf(childIds, id)) : childIds,
    teacherId: requiredChoice(raw, "teacherId", TEACHER_IDS, errors),
    category: requiredChoice(raw, "category", OBSERVATION_CATEGORIES, errors),
    familiarity: requiredChoice(raw, "familiarity", FAMILIARITY_OPTIONS, errors),
    concentration: requiredChoice(raw, "concentration", CONCENTRATION_OPTIONS, errors),
    observationNotes: requiredText(raw, "observationNotes", errors),
    nextSteps: text(raw.nextSteps),
    observationDate: requiredDate(raw, "observationDate", errors),
    photoFileUrl: optionalPhoto(raw, errors),
  };
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value };
};

export const validateAccident = (data: unknown, knownChildIds: readonly string[] | null): Result<AccidentInput> => {
  const raw = isRecord(data) ? data : {};
  const errors: FieldErrors = {};

  const childId = text(raw.childId);
  if (!childId) errors.childId = REQUIRED;
  else if (knownChildIds && !isOneOf(knownChildIds, childId)) errors.childId = "Unknown child";

  const value: AccidentInput = {
    childId,
    teacherId: requiredChoice(raw, "teacherId", TEACHER_IDS, errors),
    category: requiredChoice(raw, "category", ACCIDENT_CATEGORIES, errors),
    details: requiredText(raw, "details", errors),
    location: requiredText(raw, "location", errors),
    followUpSteps: requiredText(raw, "followUpSteps", errors),
    date: requiredDate(raw, "date", errors),
  };
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value };
};

export const validateNews = (data: unknown): Result<NewsInput> => {
  const raw = isRecord(data) ? data : {};
  const errors: FieldErrors = {};
  const value: NewsInput = {
    title: requiredText(raw, "title", errors),
    content: requiredText(raw, "content", errors),
    date: requiredDate(raw, "date", errors),
    photoFileUrl: optionalPhoto(raw, errors),
  };
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value };
};

export type FirestoreDoc = { [field: string]: string | boolean };

export interface ObservationDocs {
  /** observations/{id}: childId is the Google Forms checkbox answer, ids joined with ", ". */
  top: FirestoreDoc;
  /** children/{childId}/observations/{id}, one per child, as syncObservationsToChildren writes it. */
  perChild: { childId: string; doc: FirestoreDoc }[];
}

/** `now` is the submission time: the sheet's Timestamp column. */
export const buildObservationDocs = (input: ObservationInput, id: string, now: Date): ObservationDocs => {
  const top: FirestoreDoc = {
    id,
    timeStamp: now.toISOString(),
    childId: input.childIds.join(", "),
    teacherId: input.teacherId,
    category: input.category,
    familiarity: input.familiarity,
    concentration: input.concentration,
    observationNotes: input.observationNotes,
    nextSteps: input.nextSteps,
    observationDate: londonMidnightIso(input.observationDate),
    photoFileUrl: input.photoFileUrl,
    editUrl: "",
  };
  return {
    top,
    perChild: input.childIds.map((childId) => ({ childId, doc: { ...top, childId, parentObjectId: childId } })),
  };
};

export interface AccidentDocs {
  top: FirestoreDoc;
  perChild: { childId: string; doc: FirestoreDoc };
}

export const buildAccidentDocs = (input: AccidentInput, id: string, now: Date): AccidentDocs => {
  const top: FirestoreDoc = {
    id,
    timeStamp: now.toISOString(),
    childId: input.childId,
    teacherId: input.teacherId,
    category: input.category,
    details: input.details,
    location: input.location,
    followUpSteps: input.followUpSteps,
    date: londonMidnightIso(input.date),
    editUrl: "",
  };
  return { top, perChild: { childId: input.childId, doc: { ...top, parentObjectId: input.childId } } };
};

/** news/{id}. `ignore: false` is what the portal's news query filters on. */
export const buildNewsDoc = (input: NewsInput, id: string, now: Date): FirestoreDoc => ({
  id,
  timeStamp: now.toISOString(),
  title: input.title,
  content: input.content,
  date: londonMidnightIso(input.date),
  photoFileUrl: input.photoFileUrl,
  editUrl: "",
  ignore: false,
});

/** Same rule as the Firestore rules' isTeacher(): any @bee-happy.club address, the manager included. */
export const isTeacherEmail = (email: unknown): boolean =>
  typeof email === "string" && email.split("@")[1] === "bee-happy.club";
